export type UserPermissionDetails = {
  access: boolean;
  consumed: string[];
  remaining: number;
};

export type UserPermissions = {
  submit_solution: UserPermissionDetails;
  question_hint_or_gotcha_view: UserPermissionDetails;
  question_solution_view: UserPermissionDetails;
  question_company_view: UserPermissionDetails;
  question_company_filter: UserPermissionDetails;
  course_locked_section_view: UserPermissionDetails;
  comment_admin: UserPermissionDetails;
  post_admin: UserPermissionDetails;
  access_takehome: UserPermissionDetails;
  upload_takehome: UserPermissionDetails;
  comment_view: UserPermissionDetails;
  mock_interviews: UserPermissionDetails;
  ai_interview_access: UserPermissionDetails;
  text_submissions: UserPermissionDetails;
  view_challenge_answers: UserPermissionDetails;
};

export type UserProfile = {
  id: number;
  uuid: string;
  email: string;
  first_name: string;
  last_name: string;
  username: string;
  img_url: string;
  linkedin_url: string;
  is_confirmed: boolean;
  user_group_id: number;
  onboarding_complete: boolean;
};

export type StripeSubscription = {
  id: number;
  subscription_id: string;
  product_id: string;
  price_id: string;
  product_name: string;
  price_name: string;
  billing_interval: 'month' | 'quarter' | 'year' | 'one-time';
  start_time: number;
  end_time: number;
  trial_end_time: number;
  cancel_time: number;
};

export type AuthenticatedUser = UserProfile & {
  loggedIn: true;
  hasToConfirmEmail: boolean;
  isAdmin: boolean;
  hasSubscription: boolean;
  permissions: UserPermissions;
  subscriptions: StripeSubscription[];
};

export type PublicUser = {
  loggedIn: false;
  hasToConfirmEmail: false;
  isAdmin: false;
  hasSubscription: boolean;
  permissions: UserPermissions;
  subscriptions: StripeSubscription[];
};

export type User = AuthenticatedUser | PublicUser;

export type UserStreak = {
  streak: number;
  longest_streak: number;
  time_to_reset: number;
  time_to_next_streak: number;
};

export enum UserGroup {
  Public = 1,
  Free = 2,
  Admin = 3,
  Premium = 4,
  Coder = 7,
}

export const fallbackUserPermissions: UserPermissions = {
  submit_solution: {consumed: [], remaining: 0, access: false},
  question_hint_or_gotcha_view: {consumed: [], remaining: 0, access: false},
  question_solution_view: {consumed: [], remaining: 0, access: false},
  question_company_view: {consumed: [], remaining: 0, access: false},
  question_company_filter: {consumed: [], remaining: 0, access: false},
  course_locked_section_view: {consumed: [], remaining: 0, access: false},
  comment_admin: {consumed: [], remaining: 0, access: false},
  post_admin: {consumed: [], remaining: 0, access: false},
  access_takehome: {consumed: [], remaining: 0, access: false},
  upload_takehome: {consumed: [], remaining: 0, access: false},
  comment_view: {consumed: [], remaining: 0, access: false},
  mock_interviews: {consumed: [], remaining: 0, access: false},
  ai_interview_access: {consumed: [], remaining: 0, access: false},
  text_submissions: {consumed: [], remaining: 0, access: false},
  view_challenge_answers: {consumed: [], remaining: 0, access: false},
};

export type OAuthProvider = 'google' | 'linkedin';
